<template>
  <div class="flex flex-col gap-4 px-4 h-full">
    <!-- Preview Button -->
    <div class="flex justify-start">
      <button
        type="button"
        class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-theme-600 bg-white border border-theme-600 rounded-lg hover:bg-theme-50 transition-colors"
        @click="openPreviewModal"
        :title="$t('document-type.previewColumns')">
        <IconEyeOutline class="size-3.5" />
        {{ $t('document-type.previewColumns') }}
      </button>
    </div>

    <!-- Columns Layout -->
    <div class="flex gap-4 flex-1 overflow-hidden">
      <!-- Fields Column -->
      <div class="flex-1 bg-theme-50 rounded-lg p-2 flex flex-col">
        <h3 class="text-lg text-center font-semibold mb-2 text-gray-700">
          {{ $t('document-type.fields') }}
        </h3>
        <div class="flex-1 overflow-y-auto space-y-2 pr-2">
          <draggable
            v-if="mode === 'edit'"
            tag="ul"
            v-model="availableFields"
            :group="{ name: 'fields', pull: true, put: false, sort: false }"
            item-key="uniqueKey"
            handle=".handle"
            :move="onMoveToCommon"
            class="space-y-2">
            <template #item="{ element }">
              <li
                class="flex items-center gap-2 px-3 py-3 rounded-lg shadow-sm border text-sm font-medium transition-all duration-200 hover:shadow-md"
                :class="getCardColorClass(element.documentTypeCode)">
                <div class="h-full py-1 text-gray-400 rounded-sm cursor-move bg-white handle">
                  <IconDragVerticalSolid class="size-4" />
                </div>
                <span>{{ element.documentTypeCode }}.{{ element.fieldName }}</span>
              </li>
            </template>
          </draggable>
          <ul v-else class="space-y-2">
            <li
              v-for="element in availableFields"
              :key="element.fieldName"
              class="flex items-center gap-2 px-3 py-2 rounded-lg shadow-sm border text-sm font-medium transition-all duration-200"
              :class="getCardColorClass(element.documentTypeCode)">
              <span>{{ element.documentTypeCode }}.{{ element.fieldName }}</span>
            </li>
          </ul>
        </div>
      </div>

      <!-- Common Column -->
      <div class="flex-1 bg-theme-50 rounded-lg p-2 flex flex-col">
        <h3 class="text-lg text-center font-semibold mb-2 text-gray-700">
          {{ $t('document-type.common') }}
        </h3>
        <div class="flex-1 grid grid-rows-[auto_1fr] overflow-y-auto pr-2 gap-2">
          <!-- Fixed -->
          <ul class="space-y-2">
            <li v-for="fixedField in fixedFields" :key="fixedField.fieldName">
              <button
                type="button"
                title="properties"
                class="w-full flex items-center gap-2 px-3 py-3 rounded-lg shadow-sm border text-sm font-medium transition-all duration-200 hover:cursor-pointer hover:opacity-100 hover:shadow-md"
                :class="[
                  getCardColorClass(fixedField.documentTypeCode),
                  mode === 'edit' ? 'opacity-60' : '',
                ]"
                @click="onOpenProperties(fixedField)">
                {{ fixedField.documentTypeCode }}.{{ fixedField.fieldName }}
              </button>
            </li>
          </ul>
          <!-- Draggable -->
          <draggable
            v-if="mode === 'edit'"
            tag="ul"
            v-model="commonFields"
            :group="{ name: 'fields', pull: false, put: true }"
            item-key="uniqueKey"
            handle=".handle"
            class="space-y-2 h-full">
            <template #item="{ element }">
              <li
                class="flex items-center gap-2 px-3 py-2 rounded-lg shadow-sm border text-sm font-medium transition-all duration-200 hover:shadow-md hover:cursor-pointer"
                :class="[getCardColorClass(element.documentTypeCode)]"
                @click="onOpenProperties(element)">
                <span class="flex-1">{{ element.documentTypeCode }}.{{ element.fieldName }}</span>
                <button
                  type="button"
                  class="text-gray-800 p-1 rounded-full hover:bg-gray-50"
                  @click.stop="removeFromCommon(element)"
                  :title="$t('document-type.remove')">
                  <IconXmark class="size-5" />
                </button>
              </li>
            </template>
          </draggable>
          <ul v-else class="space-y-2">
            <li
              v-for="element in commonFields"
              :key="element.fieldName"
              class="flex items-center gap-2 px-3 py-2 rounded-lg shadow-sm border text-sm font-medium transition-all duration-200"
              :class="getCardColorClass(element.documentTypeCode)">
              <span>{{ element.documentTypeCode }}.{{ element.fieldName }}</span>
            </li>
          </ul>
        </div>
      </div>

      <!-- Specific Column -->
      <div class="flex-1 bg-theme-50 rounded-lg p-2 flex flex-col">
        <h3 class="text-lg text-center font-semibold mb-2 text-gray-700">
          {{ $t('document-type.specific') }}
        </h3>
        <div class="flex-1 grid grid-rows-[auto_1fr] overflow-y-auto pr-2 gap-2">
          <!-- Fixed -->
          <ul class="space-y-2">
            <template v-for="field in specificFields">
              <li
                v-if="field.isFixed"
                :key="field.fieldName"
                class="flex items-center gap-2 px-3 py-3 rounded-lg shadow-sm border text-sm font-medium transition-all duration-200 hover:cursor-pointer hover:shadow-md hover:opacity-100"
                :class="[
                  getCardColorClass(field.documentTypeCode),
                  mode === 'edit' ? 'opacity-60' : '',
                ]"
                @click="onOpenProperties(field)">
                <span class="flex-1">{{ field.documentTypeCode }}.{{ field.fieldName }}</span>
              </li>
            </template>
          </ul>
          <!-- Draggable -->
          <draggable
            v-if="mode === 'edit'"
            tag="ul"
            v-model="specificFields"
            :group="{ name: 'fields', pull: false, put: true }"
            item-key="uniqueKey"
            class="space-y-2 h-full">
            <template #item="{ element }">
              <li
                v-if="!element.isFixed"
                class="flex items-center gap-2 px-3 py-2 rounded-lg shadow-sm text-sm font-medium transition-all duration-200 hover:shadow-md hover:cursor-pointer"
                :class="[
                  getCardColorClass(element.documentTypeCode),
                  element.isFixed ? 'opacity-60' : '',
                ]"
                @click="onOpenProperties(element)">
                <span class="flex-1">{{ element.documentTypeCode }}.{{ element.fieldName }}</span>
                <button
                  v-if="!element.isFixed"
                  type="button"
                  class="text-gray-800 p-1 rounded-full hover:bg-gray-50"
                  @click.stop="removeFromSpecific(element)"
                  :title="$t('document-type.remove')">
                  <IconXmark class="size-5" />
                </button>
              </li>
            </template>
          </draggable>
          <ul v-else class="space-y-2">
            <template v-for="element in specificFields" :key="element.fieldName">
              <li
                v-if="!element.isFixed"
                class="flex items-center gap-2 px-3 py-2 rounded-lg shadow-sm border text-sm font-medium transition-all duration-200"
                :class="getCardColorClass(element.documentTypeCode)">
                <span>{{ element.documentTypeCode }}.{{ element.fieldName }}</span>
              </li>
            </template>
          </ul>
        </div>
      </div>
      <DocumentTypeDetailsDefinitionsModalProperties
        v-if="propertiesModalActive"
        :mode="mode"
        :field="selectedField"
        @close="propertiesModalActive = false"
        @save="onSaveProperties" />

      <DocumentTypeDetailsDefinitionsModalPreview
        v-if="previewModalActive"
        :column-definitions-json="serializeColumnDefinitions()"
        @close="previewModalActive = false" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, type Ref } from 'vue';
import { getDocumentTypeColumnDefinitionFields } from '@/services/api/documents';
import draggable from 'vuedraggable';
import IconDragVerticalSolid from '@/assets/icons/IconDragVerticalSolid.vue';
import IconXmark from '@/assets/icons/IconXmark.vue';
import IconEyeOutline from '@/assets/icons/IconEyeOutline.vue';
import DocumentTypeDetailsDefinitionsModalProperties from './DocumentTypeDetailsDefinitionsModalProperties.vue';
import DocumentTypeDetailsDefinitionsModalPreview from './DocumentTypeDetailsDefinitionsModalPreview.vue';
import type {
  ColumnField,
  DocumentType,
  ColumnDefinitionType,
  ColumnFieldDefinition,
  DocumentDetailDefinition,
} from '@/types/DocumentTypes';
import { useToastStore } from '@/stores/toast';

const props = defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout: boolean;
}>();

const data = defineModel<DocumentType>();

const toastStore = useToastStore();

const columnDefinitionFields = ref<ColumnField[]>([]);
const availableFieldsRaw = ref<ColumnField[]>([]);
const commonFields = ref<ColumnField[]>([]);
const fixedFields = ref<ColumnField[]>([]);
const specificFields = ref<ColumnField[]>([]);
const propertiesModalActive = ref(false);
const previewModalActive = ref(false);

const availableFields = computed({
  get: () => {
    const commonFieldNames = new Set(commonFields.value.map((f) => f.fieldName));
    const restrictedFieldNames = new Set(['MaterialName', 'MaterialCode']);
    return availableFieldsRaw.value.filter(
      (field) =>
        !commonFieldNames.has(field.fieldName) && !restrictedFieldNames.has(field.fieldName)
    );
  },
  set: (value) => {
    availableFieldsRaw.value = value;
  },
});

watch(
  () => data.value?.id,
  () => {
    loadColumnDefinitionFields();
  },
  { immediate: true }
);

// Deserialize ColumnDefinitions JSON string into structured data
function deserializeColumnDefinitions(): ColumnDefinitionType | null {
  if (!data.value?.columnDefinitions) return null;

  try {
    const parsed = JSON.parse(data.value.columnDefinitions) as ColumnDefinitionType;
    return parsed;
  } catch (error) {
    console.error('Failed to parse column definitions JSON:', error);
    return null;
  }
}

// Convert ColumnFieldDefinition to ColumnField format
function convertToColumnField(
  fieldDef: ColumnFieldDefinition,
  documentTypeCode: string,
  documentTypeId: string
): ColumnField {
  return {
    documentTypeCode,
    documentTypeId,
    fieldName: fieldDef.FieldName,
    uniqueKey: `${documentTypeCode}.${fieldDef.FieldName}`,
    properties: {
      order: fieldDef.OrderNumber,
      width: fieldDef.Width,
      caption: fieldDef.Caption,
    },
  };
}

// Convert ColumnField back to ColumnFieldDefinition format
function convertToColumnFieldDefinition(field: ColumnField): ColumnFieldDefinition {
  return {
    FieldName: field.fieldName,
    Caption: field.properties?.caption || `${field.documentTypeCode}.${field.fieldName}`,
    OrderNumber: field.properties?.order || 0,
    Width: field.properties?.width || 100,
    ForegroundColor: '',
    BackroundColor: '',
    Lookup: {
      LookupEntity: '',
      LookupKey: '',
      LookupResult: '',
    },
    Control: '',
    Format: '',
    UseFilter: true,
  };
}

// Serialize current UI state back to ColumnDefinitionType JSON
function serializeColumnDefinitions(): string {
  if (!data.value?.id || !data.value?.code) return data.value?.columnDefinitions || '';

  // Group fields by document type code
  const fieldsByDocumentTypeMap = new Map<
    string,
    { common: ColumnField[]; specific: ColumnField[] }
  >();

  // Combine fixedFields and commonFields for serialization
  const allCommonForSave = [...fixedFields.value, ...commonFields.value];
  allCommonForSave.forEach((field) => {
    if (!fieldsByDocumentTypeMap.has(field.documentTypeCode))
      fieldsByDocumentTypeMap.set(field.documentTypeCode, { common: [], specific: [] });
    fieldsByDocumentTypeMap.get(field.documentTypeCode)!.common.push(field);
  });
  specificFields.value.forEach((field) => {
    if (!fieldsByDocumentTypeMap.has(field.documentTypeCode))
      fieldsByDocumentTypeMap.set(field.documentTypeCode, { common: [], specific: [] });
    fieldsByDocumentTypeMap.get(field.documentTypeCode)!.specific.push(field);
  });

  // Create DocumentDetails array with proper ordering
  const documentDetails: DocumentDetailDefinition[] = [];

  // Sort document types: current first, then others alphabetically
  const sortedDocumentTypeCodes = Array.from(fieldsByDocumentTypeMap.keys()).sort((a, b) => {
    if (a === data.value?.code && b !== data.value?.code) return -1;
    if (a !== data.value?.code && b === data.value?.code) return 1;
    return a.localeCompare(b);
  });

  sortedDocumentTypeCodes.forEach((documentTypeCode) => {
    const fields = fieldsByDocumentTypeMap.get(documentTypeCode)!;
    const isCurrentType = documentTypeCode === data.value?.code;

    // Get documentTypeId from the first field that has it, or use current document ID for current type
    const documentTypeId = isCurrentType
      ? data.value!.id!
      : fields.common.find((f) => f.documentTypeId)?.documentTypeId ||
        fields.specific.find((f) => f.documentTypeId)?.documentTypeId ||
        '';

    documentDetails.push({
      DocumentTypeID: documentTypeId,
      DocumentTypeName: documentTypeCode,
      IsMaster: isCurrentType,
      CommonFields: fields.common.map(convertToColumnFieldDefinition),
      SpecificFields: fields.specific.map(convertToColumnFieldDefinition),
    });
  });

  const columnDefinitionType: ColumnDefinitionType = {
    DocumentTypeID: data.value.id,
    DocumentTypeName: data.value.code,
    DocumentHeader: {},
    DocumentDetails: documentDetails,
  };

  return JSON.stringify(columnDefinitionType);
}

// Save current column definitions to the DocumentType model
function saveColumnDefinitions() {
  if (!data.value) return;

  const serializedJson = serializeColumnDefinitions();
  data.value.columnDefinitions = serializedJson;
}

defineExpose({
  saveColumnDefinitions,
});

async function loadColumnDefinitionFields() {
  if (data.value?.id) {
    const fields = await getDocumentTypeColumnDefinitionFields(data.value.id);
    columnDefinitionFields.value = fields.map((field) => ({
      ...field,
      uniqueKey: `${field.documentTypeCode}.${field.fieldName}`,
    }));
    initializeColumns();
  }
}

function initializeColumns() {
  const columnDefinitions = deserializeColumnDefinitions();
  if (columnDefinitions) initializeFromColumnDefinitions(columnDefinitions);
  else initializeFromDefaults();
}

function getFixedFieldsForCurrentType(): ColumnField[] {
  if (!data.value?.code || !data.value?.id) return [];

  return [
    {
      documentTypeCode: data.value.code,
      documentTypeId: data.value.id,
      fieldName: 'MaterialCode',
      isFixed: true,
      uniqueKey: `${data.value.code}.MaterialCode`,
    },
    {
      documentTypeCode: data.value.code,
      documentTypeId: data.value.id,
      fieldName: 'MaterialName',
      isFixed: true,
      uniqueKey: `${data.value.code}.MaterialName`,
    },
  ];
}

function initializeFromColumnDefinitions(columnDefinitions: ColumnDefinitionType) {
  const sortedDocumentDetails = [...columnDefinitions.DocumentDetails].sort((a, b) => {
    const aIsCurrent = a.DocumentTypeID === data.value?.id;
    const bIsCurrent = b.DocumentTypeID === data.value?.id;

    if (aIsCurrent && !bIsCurrent) return -1;
    if (!aIsCurrent && bIsCurrent) return 1;
    return a.DocumentTypeName.localeCompare(b.DocumentTypeName);
  });

  const allCommonFields: ColumnField[] = [];
  const allSpecificFields: ColumnField[] = [];

  sortedDocumentDetails.forEach((documentDetail) => {
    const commonFieldsForType = documentDetail.CommonFields.map((fieldDef) => {
      const field = convertToColumnField(
        fieldDef,
        documentDetail.DocumentTypeName,
        documentDetail.DocumentTypeID
      );
      return {
        ...field,
        uniqueKey: `${field.documentTypeCode}.${field.fieldName}`,
      };
    });
    allCommonFields.push(...commonFieldsForType);

    const specificFieldsForType = documentDetail.SpecificFields.map((fieldDef) => {
      const field = convertToColumnField(
        fieldDef,
        documentDetail.DocumentTypeName,
        documentDetail.DocumentTypeID
      );
      // Mark quantity fields as fixed
      if (field.fieldName.toLowerCase() === 'quantity') {
        field.isFixed = true;
      }
      return {
        ...field,
        uniqueKey: `${field.documentTypeCode}.${field.fieldName}`,
      };
    });
    allSpecificFields.push(...specificFieldsForType);
  });

  // Remove duplicates from allCommonFields (in case JSON has duplicates)
  const uniqueCommonFields = allCommonFields.filter(
    (field, index, self) =>
      index ===
      self.findIndex(
        (f) => f.documentTypeCode === field.documentTypeCode && f.fieldName === field.fieldName
      )
  );

  // Ensure fixed fields are present in commonFields for current document type
  const requiredFixedFields = getFixedFieldsForCurrentType();
  requiredFixedFields.forEach((fixedField) => {
    const exists = uniqueCommonFields.some(
      (f) =>
        f.documentTypeCode === fixedField.documentTypeCode && f.fieldName === fixedField.fieldName
    );
    if (!exists) {
      uniqueCommonFields.unshift({
        ...fixedField,
        uniqueKey: `${fixedField.documentTypeCode}.${fixedField.fieldName}`,
      });
    } else {
      // Mark existing field as fixed
      const existingField = uniqueCommonFields.find(
        (f) =>
          f.documentTypeCode === fixedField.documentTypeCode && f.fieldName === fixedField.fieldName
      );
      if (existingField) {
        existingField.isFixed = true;
      }
    }
  });

  // Separate fixed fields from common fields
  fixedFields.value = uniqueCommonFields.filter((f) => f.isFixed);
  commonFields.value = uniqueCommonFields.filter((f) => !f.isFixed);
  specificFields.value = allSpecificFields;

  // Available fields = all fields minus those already assigned to common/specific
  const assignedFieldKeys = new Set([
    ...fixedFields.value.map((f) => `${f.documentTypeCode}.${f.fieldName}`),
    ...commonFields.value.map((f) => `${f.documentTypeCode}.${f.fieldName}`),
    ...allSpecificFields.map((f) => `${f.documentTypeCode}.${f.fieldName}`),
  ]);

  availableFieldsRaw.value = columnDefinitionFields.value
    .map((field, index) => ({ ...field, originalIndex: index }))
    .filter((field) => !assignedFieldKeys.has(`${field.documentTypeCode}.${field.fieldName}`));
}

function initializeFromDefaults() {
  const quantityFields: ColumnField[] = [];
  const otherFields: ColumnField[] = [];

  columnDefinitionFields.value.forEach((field, index) => {
    const fieldWithIndex = {
      ...field,
      originalIndex: index,
      uniqueKey: `${field.documentTypeCode}.${field.fieldName}`,
    };
    if (fieldWithIndex.fieldName.toLowerCase() === 'quantity') {
      fieldWithIndex.isFixed = true;
      quantityFields.push(fieldWithIndex);
    } else {
      otherFields.push(fieldWithIndex);
    }
  });

  specificFields.value = quantityFields;
  commonFields.value = [];
  fixedFields.value = getFixedFieldsForCurrentType().map((f) => ({
    ...f,
    uniqueKey: `${f.documentTypeCode}.${f.fieldName}`,
  }));
  availableFieldsRaw.value = otherFields;
}

let dragToastShown = false;
const RESTRICTED_COMMON_FIELDS = ['TotalQuantity', 'NetTotal', 'GrossTotal', 'LineVatTotal'];

function onMoveToCommon(evt: any): boolean {
  const draggedItem = evt.draggedContext.element as ColumnField;
  const destinationList = evt.relatedContext?.list;

  // Not moving to common? → Allow
  // document type not parent? → Block
  // Restricted field? → Block
  // In specific? → Block with toast

  if (destinationList !== commonFields.value) return true;
  if (draggedItem.documentTypeCode !== data.value?.code) return false;
  const isRestrictedField = RESTRICTED_COMMON_FIELDS.includes(draggedItem.fieldName);
  if (isRestrictedField) return false;
  const existsInSpecific = specificFields.value.some((f) => f.fieldName === draggedItem.fieldName);
  if (!existsInSpecific) return true;
  if (!dragToastShown) {
    dragToastShown = true;
    toastStore.error({
      text: 'Cannot move field to Common. Remove it from Specific column first.',
      timeout: 6000,
    });
    setTimeout(() => {
      dragToastShown = false;
    }, 1000);
  }
  return false;
}

function removeFieldFromList(sourceList: Ref<ColumnField[]>, field: ColumnField) {
  const index = sourceList.value.findIndex((f) => {
    if (f.uniqueKey && field.uniqueKey) {
      return f.uniqueKey === field.uniqueKey;
    }
    return f.documentTypeCode === field.documentTypeCode && f.fieldName === field.fieldName;
  });
  if (index === -1) return;

  const removedField = sourceList.value[index];
  sourceList.value.splice(index, 1);

  const alreadyInAvailable = availableFieldsRaw.value.some((f) => {
    if (f.uniqueKey && removedField.uniqueKey) {
      return f.uniqueKey === removedField.uniqueKey;
    }
    return (
      f.documentTypeCode === removedField.documentTypeCode && f.fieldName === removedField.fieldName
    );
  });

  if (alreadyInAvailable) {
    return;
  }

  // Insert item back to it's original index
  if (removedField.originalIndex !== undefined) {
    const insertIndex = availableFields.value.findIndex(
      (f) => f.originalIndex !== undefined && f.originalIndex > removedField.originalIndex!
    );

    if (insertIndex === -1) {
      availableFieldsRaw.value.push(removedField);
    } else {
      availableFieldsRaw.value.splice(insertIndex, 0, removedField);
    }
  } else {
    availableFieldsRaw.value.push(removedField);
  }
}

function removeFromCommon(field: ColumnField) {
  removeFieldFromList(commonFields, field);
}

function removeFromSpecific(field: ColumnField) {
  removeFieldFromList(specificFields, field);
}

const selectedField = ref<ColumnField>();
function onOpenProperties(field: ColumnField) {
  selectedField.value = field;
  propertiesModalActive.value = true;
}

function onSaveProperties(updatedField: ColumnField) {
  if (!selectedField.value) return;
  // Find and update in fixedFields
  const fixedIndex = fixedFields.value.findIndex(
    (f) =>
      f.fieldName === selectedField.value!.fieldName &&
      f.documentTypeCode === selectedField.value!.documentTypeCode
  );

  if (fixedIndex !== -1) {
    fixedFields.value[fixedIndex] = updatedField;
    selectedField.value = updatedField;
    propertiesModalActive.value = false;
    return;
  }

  // Find and update in commonFields
  const commonIndex = commonFields.value.findIndex(
    (f) =>
      f.fieldName === selectedField.value!.fieldName &&
      f.documentTypeCode === selectedField.value!.documentTypeCode
  );

  if (commonIndex !== -1) {
    commonFields.value[commonIndex] = updatedField;
    selectedField.value = updatedField;
    propertiesModalActive.value = false;
    return;
  }

  // Find and update in specificFields
  const specificIndex = specificFields.value.findIndex(
    (f) =>
      f.fieldName === selectedField.value!.fieldName &&
      f.documentTypeCode === selectedField.value!.documentTypeCode
  );

  if (specificIndex !== -1) {
    specificFields.value[specificIndex] = updatedField;
    selectedField.value = updatedField;
    propertiesModalActive.value = false;
    return;
  }

  propertiesModalActive.value = false;
}

function openPreviewModal() {
  previewModalActive.value = true;
}

watch(
  [commonFields, fixedFields, specificFields],
  () => {
    if (props.mode === 'edit') {
      saveColumnDefinitions();
    }
  },
  { deep: true }
);

const documentTypeColorMap = new Map<string, string>();
let colorIndex = 0;

function getCardColorClass(documentTypeCode: string): string {
  const colors = [
    'bg-theme-100 border-theme-200 text-theme-800',
    'bg-green-100 border-green-200 text-green-800',
    'bg-purple-100 border-purple-200 text-purple-800',
    'bg-orange-100 border-orange-200 text-orange-800',
    'bg-pink-100 border-pink-200 text-pink-800',
    'bg-indigo-100 border-indigo-200 text-indigo-800',
    'bg-teal-100 border-teal-200 text-teal-800',
    'bg-red-100 border-red-200 text-red-800',
  ];

  if (!documentTypeColorMap.has(documentTypeCode)) {
    documentTypeColorMap.set(documentTypeCode, colors[colorIndex % colors.length]);
    colorIndex++;
  }

  return documentTypeColorMap.get(documentTypeCode)!;
}
</script>
